import _ from 'lodash';
import { GridOptions, ValueGetterParams, IAggFunc } from '@ag-grid-enterprise/all-modules';
import moment from 'moment';
import {
  AlphaNumericComparator,
  BasicCellRenderer,
  BasicDateValueFormatter,
  BooleanCellRenderer,
  BranchArrayFormatter,
  ColorCellRenderer,
  CountryCellRenderer,
  CurrencyAggFunc,
  CurrencyCountryCellRenderer,
  CurrencyFormatter,
  DateComparator,
  FromNowValueFormatter,
  ImageCellRenderer,
  MoreOptionsRenderer,
  NumberComparator,
  SmartSortComparator,
  StatusCellRenderer,
  StatusCodeComparator,
  StatusCodeRenderer,
  StatusComparator,
  UserValueFormatter,
} from '../../util/grid';
import { extractTimestamp } from '../../util/general';

const getGridOptions = (): GridOptions => {
  return {
    defaultColDef: {
      enableCellChangeFlash: true,
      resizable: true,
      sortable: true,
      filter: true,
    },
    // undoRedoCellEditing: true,
    enableRangeSelection: true,
    components: {
      basicCellRenderer: BasicCellRenderer as any,
      booleanCellRenderer: BooleanCellRenderer as any,
      colorCellRenderer: ColorCellRenderer as any,
      countryCellRenderer: CountryCellRenderer as any,
      currencyCountryCellRenderer: CurrencyCountryCellRenderer as any,
      imageCellRenderer: ImageCellRenderer as any,
      moreOptionsRenderer: MoreOptionsRenderer as any,
      statusCellRenderer: StatusCellRenderer as any,
      statusCodeRenderer: StatusCodeRenderer as any,
    },
    aggFuncs: {
      currencyAggFunc: CurrencyAggFunc as unknown as IAggFunc,
    },
    icons: {
      clipboardCopy: '<i class="fad fa-copy fa-fw"/>',
    },
    // groupUseEntireRow: true,
    columnTypes: {
      repairOrder: {
        comparator: SmartSortComparator,
      },
      statusCode: {
        cellRenderer: 'statusCodeRenderer',
        comparator: StatusCodeComparator,
      },
      numberColumn: {
        comparator: NumberComparator,
        filter: 'agNumberColumnFilter',
      },
      number: {
        comparator: NumberComparator,
        filter: 'agNumberColumnFilter',
      },
      pennyColumn: {
        valueFormatter: (data: any) => {
          return (data.value / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        },
      },
      secondsToLocale: {
        valueFormatter: (data: any) => {
          return new Date(parseInt(data.value) * 1000).toLocaleString();
        },
      },
      booleanColumn: {
        cellRenderer: 'booleanCellRenderer',
      },
      branchColumn: {
        valueFormatter: (data: any) => {
          if (data.value && data.value.name) {
            return data.value.name;
          }

          if (data.data && data.data.branchId === 0) {
            return 'All Branches';
          }

          return '';
        },
      },
      branchArrayColumn: {
        valueFormatter: BranchArrayFormatter,
      },
      quoteColumn: {
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data) {
            return;
          }
          return `Q${_.get(params, 'data.id')}`;
        },
        comparator: AlphaNumericComparator,
      },
      dealColumn: {
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data) {
            return;
          }
          return `D${_.get(params, 'data.QuoteDeal.id')}`;
        },
        comparator: AlphaNumericComparator,
      },
      colorColumn: {
        cellRenderer: 'colorCellRenderer',
      },
      countryColumn: {
        cellRenderer: 'countryCellRenderer',
      },
      currencyCountryColumn: {
        cellRenderer: 'currencyCountryCellRenderer',
      },
      currencyColumn: {
        valueFormatter: CurrencyFormatter,
        comparator: (a: any, b: any) => {
          return Number(a || 0) - Number(b || 0);
        },
        aggFunc: 'currencyAggFunc',
      },
      statusColumn: {
        cellRenderer: 'statusCellRenderer',
        comparator: StatusComparator,
        keyCreator: (params: any) => params.value?.name || params.value,
      },
      imageColumn: {
        cellRenderer: 'imageCellRenderer',
      },
      fromNowColumn: {
        valueFormatter: FromNowValueFormatter,
      },
      basicDateColumn: {
        valueFormatter: BasicDateValueFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: DateComparator,
          inRangeInclusive: true,
        },
      },

      userColumn: {
        keyCreator: (params: any) => {
          if (params.value) {
            return `${params.value.firstName} ${params.value.lastName}`;
          }
          return '';
        },
        valueFormatter: UserValueFormatter,
      },
      jsonInvoiceColumn: {
        valueGetter: (params: ValueGetterParams) => {
          const invoices = _.get(params.data, params.column.getColId());
          if (!invoices || !invoices.length) {
            return '';
          }

          return invoices
            .map((x: any) => {
              if (!x.originalInvoiceNum) {
                return x.invoiceNum;
              } else {
                return `${x.invoiceNum} > ${x.originalInvoiceNum}`;
              }
            })
            .join(', ');
        },
      },
      flakeDate: {
        filter: 'agDateColumnFilter',
        filterParams: {
          inRangeInclusive: true,
          comparator: (filterLocalDateAtMidnight: any, cellValue: string) => {
            if (cellValue == null) {
              return 0;
            }

            const cellMoment = moment(extractTimestamp(cellValue));
            // const endOfDay = cellMoment.endOf('day').toDate();
            const startOfDay = cellMoment.startOf('day').toDate();

            // Now that both parameters are Date objects, we can compare
            if (startOfDay < filterLocalDateAtMidnight) {
              return -1;
            } else if (startOfDay > filterLocalDateAtMidnight) {
              return 1;
            }

            return 0;
          },
        },
        valueFormatter: (params: any) => {
          if (!params.value) {
            return '';
          }

          return moment(extractTimestamp(params.value)).format('MMMM Do YYYY, h:mm a');
        },
      },
    },
  };
};

export default getGridOptions;
